import { useState, useEffect } from 'react';
import type { PowerEnvYearConfig } from '../lib/envSchemeRegistry';
import { sysConfigApiService } from '../services/sysConfigApiService';

// Year-wise: displayKey (e.g. "LREC", "VEEC/ESS/REES") -> { "2026": 16.67, ... }. Resolve a value
// out of this with resolvePowerPct(config, displayKey, year) from envSchemeRegistry.ts.
const HARDCODED_DEFAULTS: PowerEnvYearConfig = {
  LREC: { '2026': 16.67 },
  SREC: { '2026': 11.67 },
  ESC: { '2026': 11 },
  'VEEC/ESS/REES': { '2026': 9.71 },
  EEIS: { '2026': 8.6 },
};

// Module-level cache so every component shares the same fetch
let _cached: PowerEnvYearConfig | null = null;
let _promise: Promise<PowerEnvYearConfig> | null = null;

async function fetchDefaults(): Promise<PowerEnvYearConfig> {
  if (_cached) return _cached;
  if (_promise) return _promise;

  _promise = sysConfigApiService.get('powerEnvironment').then(result => {
    const schemes = (result.success ? (result.data?.value as any)?.schemes : undefined) as PowerEnvYearConfig | undefined;
    _cached = (schemes && typeof schemes === 'object') ? schemes : { ...HARDCODED_DEFAULTS };
    _promise = null;
    return _cached;
  }).catch(() => {
    _promise = null;
    return { ...HARDCODED_DEFAULTS };
  });

  return _promise;
}

/**
 * The year-wise power% defaults, AND whether they are the real ones yet.
 *
 * ⚠️ `loaded` IS LOAD-BEARING. THE FALLBACK TABLE ABOVE HOLDS ONLY 2026.
 *
 * HARDCODED_DEFAULTS exists so a failed sys_config fetch still yields something usable, but it
 * has a single year per scheme. resolvePowerPct carries the most recent configured year forward,
 * so asking it for 2027 against that table returns 2026's number — LREC 16.67 for every year of
 * every term, where the real table escalates 16.67 / 19.17 / 18.98 / 18.79.
 *
 * That matters because a caller that BAKES a derived value cannot undo it later: once 16.67 has
 * been written into an empty power_percentage, the field is no longer empty, so re-deriving when
 * the real table arrives finds nothing to fill. AGL is the worst case — its parsing prompt
 * stores `power_percentage: null` for every environmental charge by design, so the default is
 * the only source there is, and a bake that raced the fetch froze all four years at 16.67 and
 * carried that into `rate = cert x pct / 1000`.
 *
 * So anything that WRITES a default must wait for `loaded`. Anything that merely displays one
 * can use the fallback happily.
 */
export const usePowerEnvDefaultsState = (): { defaults: PowerEnvYearConfig; loaded: boolean } => {
  const [state, setState] = useState<{ defaults: PowerEnvYearConfig; loaded: boolean }>(
    _cached ? { defaults: _cached, loaded: true } : { defaults: HARDCODED_DEFAULTS, loaded: false },
  );

  useEffect(() => {
    if (_cached) {
      // Already fetched by another component — adopt it rather than sitting on the fallback.
      setState({ defaults: _cached, loaded: true });
      return;
    }
    let mounted = true;
    fetchDefaults().then(defaults => {
      // `loaded` means the fetch SETTLED, not that it succeeded: on failure fetchDefaults
      // resolves with the fallback, and waiting forever for a row that is missing would stop
      // every derived value from ever being written.
      if (mounted) setState({ defaults, loaded: true });
    });
    return () => { mounted = false; };
  }, []);

  return state;
};

/**
 * Returns the full year-wise power% defaults config from sys_config.powerEnvironment.
 * Falls back to hardcoded 2026 values until loaded (or if the row is missing/malformed).
 * Callers resolve a specific scheme+year with resolvePowerPct() from envSchemeRegistry.ts.
 *
 * Use usePowerEnvDefaultsState instead when the value will be WRITTEN into offer data — see
 * the warning there about the fallback table holding only one year.
 */
export const usePowerEnvDefaults = (): PowerEnvYearConfig =>
  usePowerEnvDefaultsState().defaults;
